/**
 * V2 admin — Settings tab (V1 admin-settings.js parity).
 *
 * Read-only mode, the site banner, and email notifications (recipients
 * + per-event toggles + test email) via the notification-settings and
 * notifications PHP endpoints, served same-origin from /adventure/api.
 */
import * as gateway from '../../core/gateway.js';
import { escapeHtml } from '../../../assets/js/utils.js';
import { showToast } from '../../ui/toast.js';
import { invalidate } from '../../core/query.js';
import { getApiBase } from '../../core/config.js';

const API_BASE = getApiBase();

/** The per-event toggles, in display order. Keys match the PHP store. */
const TOGGLES = [
  ['notify_new_user', 'New explorer registrations'],
  ['notify_new_yoink', 'New yoinks'],
  ['notify_edited_yoink', 'Edited yoinks'],
  ['notify_milestones', '🎉 Milestone celebrations (10, 25, 50, 100…)'],
  ['notify_reported_closed', '🚧 Places visitors report as gone (daily, only when Google disagrees)'],
  ['notify_feedback', '💬 Messages from the footer’s Tell Us box'],
  ['bcc_user_emails', '👀 BCC me on user emails (welcome, first yoink, digest)'],
  ['send_weekly_digest', '🗞️ Weekly digest email to users'],
];

/**
 * Squash an HTML/plain error page into one readable line. Enough of the
 * server's own words to act on, short enough to sit under the checkboxes.
 */
export function excerpt(text, limit = 220) {
  const flat = String(text || '')
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return flat.length > limit ? `${flat.slice(0, limit)}…` : flat;
}

/**
 * Explain a failed read in terms of what to do about it. A 403 and a 500
 * look identical in the panel but need opposite fixes, so name the status
 * and quote whatever the server said.
 */
export function notifErrorHtml(error) {
  const status = error?.status;
  const said = error?.message ? `<p class="admin-muted">The server said: <code>${escapeHtml(error.message)}</code></p>` : '';
  if (status === 403 || status === 401) {
    // The endpoint takes the Supabase admin token this console already
    // holds, so reaching here means the session is missing or not an
    // admin one — signing out and back in is the fix, not a second login.
    return `<p class="admin-muted">The settings endpoint wouldn’t accept this session.
      Sign out and back in, then reopen this tab. If it keeps happening, a WordPress
      admin session at <code>/wp-admin</code> in this browser also works.</p>${said}`;
  }
  if (status >= 500) {
    return `<p class="admin-muted">The settings endpoint errored (${status}). Open
      <code>/?notif-status=1</code> for the server-side diagnosis.</p>${said}`;
  }
  if (!status) {
    return `<p class="admin-muted">Couldn’t reach the settings endpoint at all.</p>${said}`;
  }
  return `<p class="admin-muted">Couldn’t read the notification settings (${status}).</p>${said}`;
}

/**
 * Where the endpoint says these settings are stored. Shown on the page
 * because the only way to check this is on an iPad with no DevTools —
 * `file` means the durable option hasn't been written yet, and anything
 * else means WordPress isn't answering and nothing will stick.
 */
export function storageNote(storage) {
  if (storage === 'option') return 'Saved in the site database.';
  if (storage === 'file') return 'Reading the legacy settings file — the next save moves these to the database.';
  if (storage === 'defaults') return 'Nothing saved yet — these are the defaults.';
  return '';
}

export function mount(el, ctx) {
  const { store } = ctx;
  let destroyed = false;
  let loaded = false;
  let settings = null; // app_settings row
  let notif = null; // notification settings from PHP
  let notifError = null; // {status, message} when the endpoint wouldn't answer

  /**
   * The Supabase access token, sent as a Bearer header so the PHP endpoint
   * can authenticate this admin the same way the rest of the console does
   * (see inc/supabase-admin-auth.php). Same shape as the purge-user call
   * in users.js.
   */
  async function authHeaders(base = {}) {
    const session = await gateway.getSession();
    const token = session?.data?.session?.access_token;
    return token ? { ...base, Authorization: `Bearer ${token}` } : { ...base };
  }

  async function load() {
    const { data } = await gateway.rawQuery(gateway.TABLES.APP_SETTINGS, { select: '*', limit: 1 });
    settings = data?.[0] || null;
    notif = null;
    notifError = null;
    try {
      // Cache-bust and opt out explicitly. The read is a plain GET on a
      // fixed URL, which is exactly the shape a browser or a host page
      // cache will serve stale — and a stale read is indistinguishable
      // from a save that didn't stick.
      const response = await fetch(`${API_BASE}/notification-settings.php?t=${Date.now()}`, {
        headers: await authHeaders(),
        cache: 'no-store',
      });
      const raw = await response.text();
      let body = null;
      try { body = JSON.parse(raw); } catch { body = null; }
      // A 403 (no WordPress admin session) answers with {error}, which is
      // still an object — rendering it would show every toggle checked and
      // every save would fail. Anything without the real settings shape is
      // a failure, and the failure has to name itself: 403 and 500 need
      // completely different fixes and there is no console to tell them
      // apart on the test device.
      if (response.ok && body && typeof body === 'object' && 'notify_new_user' in body) {
        notif = body;
      } else {
        notifError = {
          status: response.status,
          message: (body && typeof body === 'object' && body.error) || excerpt(raw),
        };
      }
    } catch (err) {
      notifError = { status: 0, message: err.message };
    }
    loaded = true;
  }

  function render() {
    if (!loaded) {
      el.innerHTML = '<div class="v2-loading">Reading the station log…</div>';
      return;
    }
    if (!settings) {
      el.innerHTML = '<div class="admin-card admin-card-span"><p class="admin-muted">No app_settings row found — create one in Supabase first.</p></div>';
      return;
    }
    const isReadOnly = settings.is_read_only === true;
    const emails = Array.isArray(notif?.admin_emails)
      ? notif.admin_emails.join(', ')
      : notif?.admin_emails || '';
    el.innerHTML = `
      <div class="admin-two-up">
        <div class="admin-card">
          <h3 class="admin-card-title">🔒 Read-only mode</h3>
          <p class="admin-muted">When enabled, explorers can browse but not create, edit, or delete. For maintenance.</p>
          <button type="button" class="admin-btn ${isReadOnly ? 'admin-btn-danger' : ''}" data-toggle-readonly>
            ${isReadOnly ? '🔒 Read-only is ON — turn off' : '🔓 Read-only is OFF — turn on'}</button>
        </div>
        <div class="admin-card">
          <h3 class="admin-card-title">📢 Banner message</h3>
          <p class="admin-muted">Shows at the top of the app for everyone.</p>
          <textarea rows="3" data-banner-text aria-label="Banner message">${escapeHtml(settings.banner_message || '')}</textarea>
          <div class="admin-confirm-actions">
            <button type="button" class="admin-btn" data-clear-banner>Clear</button>
            <button type="button" class="admin-btn admin-btn-primary" data-save-banner>Save banner</button>
          </div>
        </div>
      </div>

      <div class="admin-card admin-card-span">
        <h3 class="admin-card-title">📧 Email notifications</h3>
        ${notif ? `
          <label class="v2-field">Recipients <small>(comma-separated)</small>
            <input type="text" data-notif-emails value="${escapeHtml(emails)}"
              placeholder="email@example.com, other@example.com" autocomplete="off">
          </label>
          <div class="admin-confirm-actions admin-actions-left">
            <button type="button" class="admin-btn" data-save-emails>Save recipients</button>
            <span class="admin-muted" data-emails-status></span>
          </div>
          <div class="admin-check-grid">
            ${TOGGLES.map(([key, label]) => `
              <label class="admin-check">
                <input type="checkbox" data-notif-toggle="${key}" ${notif[key] !== false ? 'checked' : ''}>
                ${label}
              </label>`).join('')}
          </div>
          <p class="admin-muted" data-notif-status>${escapeHtml(storageNote(notif.storage))}</p>
          <div class="admin-confirm-actions admin-actions-left">
            <button type="button" class="admin-btn" data-test-email>Send test email</button>
          </div>
        ` : notifErrorHtml(notifError)}
      </div>`;
  }

  async function saveAppSettings(patch, doneMessage) {
    const { error } = await gateway.rawUpdate(gateway.TABLES.APP_SETTINGS, {
      filters: { id: `eq.${settings.id}` },
      body: { ...patch, updated_at: new Date().toISOString() },
    });
    if (destroyed) return false;
    if (error) {
      showToast('Couldn’t save the setting.', { type: 'info' });
      return false;
    }
    Object.assign(settings, patch);
    invalidate('app-settings');
    showToast(doneMessage, { type: 'success' });
    return true;
  }

  /** Put every checkbox back to the last state the server confirmed. */
  function syncToggles() {
    el.querySelectorAll('[data-notif-toggle]').forEach((box) => {
      box.checked = notif?.[box.dataset.notifToggle] !== false;
    });
  }

  /**
   * POST a settings patch. A failure must never leave the panel showing
   * the value the user picked — that is what made a broken save look like
   * a working one until the tab was reopened. On failure the controls go
   * back to the server's state and a toast says so.
   */
  async function saveNotif(body, statusSelector, { onFail } = {}) {
    const status = el.querySelector(statusSelector);
    if (status) status.textContent = 'Saving…';
    try {
      const response = await fetch(`${API_BASE}/notification-settings.php`, {
        method: 'POST',
        headers: await authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ type: 'save_settings', ...body }),
      });
      // Read as text first: when the endpoint dies the body is an HTML
      // error page, and `response.json()` throwing would leave us reporting
      // a bare status code. On an iPad the response body is otherwise
      // unreadable, so a non-JSON body becomes the error message.
      const raw = await response.text();
      let result = {};
      try {
        result = JSON.parse(raw) || {};
      } catch {
        if (!response.ok) throw new Error(excerpt(raw) || `Save failed (${response.status})`);
        throw new Error(`Server sent a non-JSON reply: ${excerpt(raw)}`);
      }
      if (!response.ok || result.success === false) throw new Error(result.error || `Save failed (${response.status})`);
      if (destroyed) return;
      if (result.settings) notif = { ...notif, ...result.settings, storage: result.storage || notif?.storage };
      if (status) {
        // A save the durable store refused is not a clean save, even
        // though the legacy file caught the value. Say which happened.
        if (result.option_error) {
          status.textContent = `⚠ Saved to the legacy file only — the database refused it: ${result.option_error}`;
        } else {
          status.textContent = '✓ Saved';
          setTimeout(() => {
            if (!destroyed && status.isConnected) status.textContent = storageNote(notif?.storage);
          }, 2000);
        }
      }
    } catch (err) {
      if (destroyed) return;
      if (status) status.textContent = `✗ ${err.message}`;
      showToast(`Couldn’t save: ${err.message}`, { type: 'info' });
      if (onFail) onFail();
    }
  }

  async function onClick(event) {
    const t = event.target;
    if (t.closest('[data-toggle-readonly]')) {
      const newValue = !settings.is_read_only;
      if (await saveAppSettings({ is_read_only: newValue }, `Read-only mode ${newValue ? 'enabled' : 'disabled'}.`)) {
        store.set({ isReadOnly: newValue });
        render();
      }
      return;
    }
    if (t.closest('[data-save-banner]') || t.closest('[data-clear-banner]')) {
      const textarea = el.querySelector('[data-banner-text]');
      if (t.closest('[data-clear-banner]') && textarea) textarea.value = '';
      const message = textarea?.value.trim() || null;
      if (await saveAppSettings({ banner_message: message }, message ? 'Banner saved.' : 'Banner cleared.')) {
        store.set({ bannerMessage: message });
        render();
      }
      return;
    }
    if (t.closest('[data-save-emails]')) {
      const emails = el.querySelector('[data-notif-emails]')?.value.trim();
      if (!emails) {
        const status = el.querySelector('[data-emails-status]');
        if (status) status.textContent = 'At least one email required';
        return;
      }
      return saveNotif({ admin_emails: emails }, '[data-emails-status]');
    }
    if (t.closest('[data-test-email]')) {
      const btn = t.closest('[data-test-email]');
      btn.disabled = true;
      btn.textContent = 'Sending…';
      try {
        const response = await fetch(`${API_BASE}/notifications.php`, {
          method: 'POST',
          headers: await authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ type: 'test' }),
        });
        const result = await response.json();
        if (!response.ok || !result.success) throw new Error(result.error || 'Failed');
        if (!destroyed) showToast('Test email sent.', { type: 'success' });
      } catch (err) {
        if (!destroyed) showToast(`Test email failed: ${err.message}`, { type: 'info' });
      }
      if (!destroyed) { btn.disabled = false; btn.textContent = 'Send test email'; }
    }
  }

  function onChange(event) {
    if (event.target.closest('[data-notif-toggle]')) {
      const body = {};
      el.querySelectorAll('[data-notif-toggle]').forEach((box) => {
        body[box.dataset.notifToggle] = box.checked;
      });
      saveNotif(body, '[data-notif-status]', { onFail: syncToggles });
    }
  }

  render();
  load().then(() => { if (!destroyed) render(); })
    .catch(() => { if (!destroyed) el.innerHTML = '<div class="v2-error">Couldn’t load settings.</div>'; });

  el.addEventListener('click', onClick);
  el.addEventListener('change', onChange);

  return () => {
    destroyed = true;
    el.removeEventListener('click', onClick);
    el.removeEventListener('change', onChange);
  };
}
